import { queryOptions, useQuery } from '@tanstack/vue-query'
import { computed, ref, unref, watch, type MaybeRef } from 'vue'

import { OlympusClient } from '@/OlympusClient'

const QUERY_KEY_PREFIX = ['phone', 'navigation-availability'] as const
const LOCATION_STATE_QUERY_KEY_PREFIX = ['phone', 'location-state'] as const
const BRAND_LOCATIONS_QUERY_KEY_PREFIX = ['phone', 'brand-locations'] as const
const PHONE_ACTIVATION_POLL_INTERVAL_MS = 2_000

export const phoneNavigationAvailable = ref(false)
export const phoneNavigationIsNew = ref(false)

export function phoneNavigationAvailabilityOptions(
  brandId: string | undefined
) {
  return queryOptions({
    queryFn: async () => {
      const queryBrandId = brandId
      if (!queryBrandId) throw new Error('Brand ID is required')
      const availabilityRequest = {
        headers: { 'X-Active-Brand-ID': queryBrandId },
        params: { brandId: queryBrandId },
      }
      const result =
        await OlympusClient.hestia.v1.brands.phone.getAvailability(
          availabilityRequest
        )
      return { brandId: queryBrandId, ...result }
    },
    queryKey: [...QUERY_KEY_PREFIX, brandId] as const,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Synchronizes Phone Routing navigation availability for a brand.
 * @param brandId Brand whose navigation state should be loaded.
 */
export function usePhoneNavigationAvailability(
  brandId: MaybeRef<string | undefined>
) {
  const query = useQuery(
    computed(() =>
      queryOptions({
        ...phoneNavigationAvailabilityOptions(unref(brandId)),
        enabled: Boolean(unref(brandId)),
        refetchInterval: (query) =>
          query.state.data?.isTransitioning
            ? PHONE_ACTIVATION_POLL_INTERVAL_MS
            : false,
      })
    )
  )

  watch(
    [computed(() => unref(brandId)), query.data],
    ([currentBrandId, result]) => {
      if (!currentBrandId || result?.brandId !== currentBrandId) {
        phoneNavigationAvailable.value = false
        phoneNavigationIsNew.value = false
        return
      }

      phoneNavigationAvailable.value = result.rolloutEnabled
      phoneNavigationIsNew.value = result.isNew
    },
    { immediate: true }
  )

  return query
}

/**
 * Loads Phone Routing state for the active location.
 * @param locationId Location whose Phone Routing state should be loaded.
 */
export function usePhoneLocationState(
  locationId: MaybeRef<string | undefined>
) {
  return useQuery({
    enabled: computed(() => Boolean(unref(locationId))),
    queryFn: async ({ queryKey }) => {
      const [, , queryLocationId] = queryKey

      if (!queryLocationId) {
        throw new Error('Location ID is required')
      }

      return await OlympusClient.hestia.v1.locations.getPhoneState({
        params: { locationId: queryLocationId },
      })
    },
    queryKey: [...LOCATION_STATE_QUERY_KEY_PREFIX, locationId] as const,
    staleTime: 10_000,
  })
}

/**
 * Loads Phone Routing state for every location in a brand.
 * @param brandId Brand whose location states should be loaded.
 */
export function usePhoneBrandLocations(brandId: MaybeRef<string | undefined>) {
  return useQuery({
    enabled: computed(() => Boolean(unref(brandId))),
    queryFn: async ({ queryKey }) => {
      const [, , queryBrandId] = queryKey

      if (!queryBrandId) {
        throw new Error('Brand ID is required')
      }

      return await OlympusClient.hestia.v1.brands.phone.getLocations({
        params: { brandId: queryBrandId },
      })
    },
    queryKey: [...BRAND_LOCATIONS_QUERY_KEY_PREFIX, brandId] as const,
    staleTime: 10_000,
  })
}
